import React, {useState, useRef, useMemo} from 'react';
import {TextInput} from '@mantine/core';
import {useQuery} from '@tanstack/react-query';
import {Pagination} from '@mantine/core';
import api from "../../utils/api.ts";
import { MagnifyingGlass as IconSearch } from "@phosphor-icons/react";
import {Tabs} from "@mantine/core";
import {Paper} from "@mantine/core";
import {useNavigate, useParams} from "react-router";
import {Col, Row} from "../../components/Flex/Flex.tsx";
import CGT from "../../utils/CGT.ts";
import Page from "../../components/Page/Page.tsx";
import {Modal} from "@mantine/core";
import LoaderScreen from "../../components/Loader/LoaderScreen.tsx";
import HtmlHighlightRenderer from "../../components/HtmlHighlightRenderer.tsx";

export interface GNS {
    id: number;
    title: string;
    author: string;
    faction?: { uid: string, name: string };
    category: string;
    rawBody: string;
    textBody: string;
    posted: number;
    location?: string;
    hacked?: boolean;
    wasedited?: boolean;
    editor?: string;
    edited?: number;
}

interface APIResponse {
    data: GNS[];
    totalRecords: number;
}
interface PaginatorState {
    first: number;
    rows: number;
    page: number;
    sortField: string;
    sortOrder: number;
    search: string;

}

const fetchGNSArchive = async (lazyState: PaginatorState, viewValue: string) => {
    try {
        const params = {
            first: lazyState.first,
            rows: lazyState.rows,
            sortField: lazyState.sortField,
            sortOrder: lazyState.sortOrder,
            // Use a global filter for the search term
            search: lazyState.search || '',
            type: viewValue,
        };
        const response = await api.get<APIResponse>('/gns/archive', {params});
        return response.data;
    } catch (error) {
        console.error("Failed to fetch news:", error);
        // Return a default structure on error to prevent crashes
        return {data: [], totalRecords: 0};
    }

}

const viewModel = [
    {label: 'Flash', value: 'flash'},
    {label: 'Posts', value: 'post'},
    {label: 'Sim News', value: 'sim'}
]


export const GNSArchive: React.FC = () => {
    const [lazyState, setLazyState] = useState<PaginatorState>({
        first: 0,
        rows: 25,
        page: 0,
        sortField: 'posted',
        sortOrder: -1, // -1 for descending, 1 for ascending
        search: '',
    });
    const navigate = useNavigate();
    const {activeTab} = useParams<{ activeTab?: string }>();

    const [openGNSPost, setOpenGNSPost] = useState<GNS | null>(null);
    const [openSimNews, setOpenSimNews] = useState<GNS | null>(null);


    const currentTabValue = useMemo(() => {
        const found = viewModel.find(item => item.value === (activeTab || 'post'));
        return found ? found.value : 'post';
    }, [activeTab]);


    // Ref to manage debouncing the search input
    const debounceTimer = useRef<number | null>(null);

    const {data, isLoading} = useQuery({
        queryKey: ['news', currentTabValue, lazyState], // React Query key, depends on the state
        queryFn: () => fetchGNSArchive(lazyState, currentTabValue),
    });

    const currentPage = Math.floor(lazyState.first / lazyState.rows) + 1;
    const totalPages = Math.ceil((data?.totalRecords ?? 0) / lazyState.rows);

    const onPage = (page: number) => {
        setLazyState(prev => ({...prev, first: (page - 1) * prev.rows}));
    };


    const onFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (debounceTimer.current) {
            window.clearTimeout(debounceTimer.current);
        }

        const value = event.target.value;
        debounceTimer.current = window.setTimeout(() => {
            setLazyState(prev => ({
                ...prev,
                first: 0, // Reset to first page on new search
                search: value,
            }));
        }, 500); // 500ms debounce delay
    };


    const onTabChange = (value: string | null) => {
        if (value) {
            navigate(activeTab ? `../${value}` : `./${value}`, {replace: false, relative: 'path'});
        }
    };


    return (
        <Page>
            <title>GNS Archive</title>
            {isLoading && <LoaderScreen message="Loading data" />}
            <Row>
                <Col grow={1}>
                    <Tabs value={currentTabValue} onChange={onTabChange}>
                        <Tabs.List>
                            {viewModel.map(item => (
                                <Tabs.Tab key={item.value} value={item.value}>{item.label}</Tabs.Tab>
                            ))}
                        </Tabs.List>
                    </Tabs>
                </Col>
                <Col>
                    <TextInput
                        leftSection={<IconSearch size={16}/>}
                        type="search"
                        onChange={onFilter}
                        placeholder="Search News..."
                    />
                </Col>
            </Row>
            <div>
            {data?.data.map((gns, index) => {
                if (currentTabValue === 'flash') {
                    return <FlashNews key={index} gns={gns}/>
                } else if (currentTabValue === 'post') {
                    return <PostNews key={index} gns={gns} onOpen={setOpenGNSPost}/>
                } else if (currentTabValue === 'sim') {
                    return <SimNews key={index} gns={gns} onOpen={setOpenSimNews}/>
                } else {
                    return null;
                }
            })}
            </div>
            <Modal
                onClose={() => setOpenGNSPost(null)}
                opened={openGNSPost !== null}
                title={openGNSPost?.title}
                size="80%"
            >
                {openGNSPost && <GNSPostDetails gns={openGNSPost} search={lazyState.search}/>}
            </Modal>
            <Modal
                onClose={() => setOpenSimNews(null)}
                opened={openSimNews !== null}
                title={openSimNews?.title}
                size="80%"
            >
                {openSimNews && <SimNewsDetails gns={openSimNews} search={lazyState.search}/>}
            </Modal>
            <div style={{display: 'flex', justifyContent: 'center', marginTop: '1rem'}}>
                <Pagination
                    value={currentPage}
                    total={totalPages}
                    onChange={onPage}
                />
            </div>
        </Page>
    );
};


const FlashNews = ({gns}: { gns: GNS }) => {
    const date = CGT.fromSeconds(gns.posted);
    return (
        <Paper shadow="xs" p="md" withBorder style={{marginBottom: '0.5rem'}}>
            <Row>
                <Col grow={1}><strong>{gns.title}</strong></Col>
                <Col><span title={date.toLocaleString(CGT.DATETIME_MED)}>{date.toCGT()}</span></Col>
            </Row>
            <p style={{marginTop: '0.25rem', marginBottom: 0}}>{gns.textBody}</p>
        </Paper>
    )
}
const PostNews = ({gns, onOpen}: { gns: GNS, onOpen: (gns: GNS) => void }) => {
    const date = CGT.fromSeconds(gns.posted);
    return (
        <Paper shadow="xs" p="md" withBorder style={{marginBottom: '0.5rem', cursor: 'pointer'}} onClick={() => onOpen(gns)}>
            <Row>
                <Col grow={1}><strong>{gns.title}</strong></Col>
                <Col><span title={date.toLocaleString(CGT.DATETIME_MED)}>{date.toCGT()}</span></Col>
            </Row>
            <i>{gns.author}{gns.faction ? ' ('+gns.faction.name+')' : null}</i>
        </Paper>
    )
}
const SimNews = ({gns, onOpen}: { gns: GNS, onOpen: (gns: GNS) => void  }) => {
    const date = CGT.fromSeconds(gns.posted);
    return (
        <Paper shadow="xs" p="md" withBorder style={{marginBottom: '0.5rem', cursor: 'pointer'}} onClick={() => onOpen(gns)}>
            <Row>
                <Col grow={1}>
                    <strong>{gns.title}</strong>
                </Col>
                <Col><span title={date.toLocaleString(CGT.DATETIME_MED)}>{date.toCGT()}</span></Col>
            </Row>
           <i>{gns.category} - {gns.author}</i>
        </Paper>
    )
}

const GNSPostDetails = ({gns, search}: { gns: GNS, search: string }) => {
    const date = CGT.fromSeconds(gns.posted);
    return (
        <div>
            <div style={{marginBottom: '0.25rem'}}><b>Category</b> {gns.category}</div>
            <div style={{marginBottom: '0.25rem'}}><b>{gns.hacked ? 'Hacked' : 'Posted'} by</b> {gns.author}{gns.faction ? ', '+gns.faction.name : null}</div>
            <div style={{marginBottom: '0.5rem'}}><b>Date: </b> {date.toCGT(false)} {gns.location}</div>

            <HtmlHighlightRenderer className="gns-details" html={gns.rawBody} highlight={search} />
        </div>
    )
}

const SimNewsDetails = ({gns, search}: { gns: GNS, search: string }) => {
    const date = CGT.fromSeconds(gns.posted);
    return (
        <div>
            <div style={{marginBottom: '0.25rem'}}><b>Posted by</b> {gns.author}</div>
            <div style={{marginBottom: '0.5rem'}}><b>Date: </b> {date.toCGT(false)}</div>
            {gns.wasedited && <div style={{marginBottom: '0.25rem'}}><b>Edited by</b> {gns.editor} on {CGT.fromSeconds(gns.edited ?? 0).toCGT(false)}</div>}

            <HtmlHighlightRenderer className="gns-details" html={gns.rawBody} highlight={search} />
        </div>
    )
}
